import React, { useContext, useEffect, useState } from 'react';
import { Box, Chip } from '@mui/material';
import { AppContext } from '../../../AppContext';
import { Actions } from '../../../enums/ActionEnums';
import { IInterest, IMembershipType } from '../../../generated/graphql';
import { Colors } from '../../../utils/theme';
import InputComponent from '../../Common/Fields/InputComponent';
import { ErrorTexts, IErrors } from './RecipientListStepper';
import ContentGrid from './ContentGrid';
import { RecipientListEnums } from '../../../enums/RecipientListEnums';
import './index.css';
import SelectAllButton from '../../Common/Buttons/SelectAllButton';

interface IProps {
  membershipTypes: IMembershipType[];
  interests: IInterest[];
  formErrors: IErrors;
  setFormErrors: (value: IErrors) => void;
}

const sidePadding = '5%';
const innerPadding = '10px';

const GeneralDetailsTab: React.FunctionComponent<IProps> = ({
  membershipTypes,
  interests,
  formErrors,
  setFormErrors
}: IProps) => {
  const { dispatch, state } = useContext(AppContext);
  const [selectedInterestsIds, setSelectedInterestsIds] = useState<string[]>(
    state.selectedRecipientList?.interestIds || []
  );
  const [selectedMembershipTypesIds, setSelectedMembershipTypesIds] = useState<string[]>(
    state.selectedRecipientList?.membershipTypeIds || []
  );
  const [currentMembershipTypes, setCurrentMembershipTypes] = useState(membershipTypes);
  const [currentInterests, setCurrentInterests] = useState(interests);
  const [name, setName] = useState(state.selectedRecipientList?.name ?? '');
  useEffect(() => {
    setCurrentMembershipTypes(membershipTypes.filter((m) => m.name != 'PENSION FUND'));
  }, [membershipTypes]);

  useEffect(() => {
    setCurrentInterests(interests);
  }, [interests]);

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = event.currentTarget;
    if (!value || value.length < 2) {
      setFormErrors({
        ...formErrors,
        name: ErrorTexts.name
      });
    } else {
      setFormErrors({ ...formErrors, name: null });
    }
    setName(value);
    dispatch({
      type: Actions.SetRecipientListName,
      payload: {
        name: value
      }
    });
  };

  const handleCheckClickedForMembershipTypes = (typeId: string) => {
    let membershipTypeIds = [];
    if (selectedMembershipTypesIds.includes(typeId)) {
      membershipTypeIds = selectedMembershipTypesIds.filter((id) => id !== typeId);
      if (!membershipTypeIds.length) {
        setFormErrors({ ...formErrors, membership: ErrorTexts.membership });
      }
      setSelectedMembershipTypesIds(membershipTypeIds);
    } else {
      membershipTypeIds = [typeId, ...selectedMembershipTypesIds];
      setFormErrors({ ...formErrors, membership: null });
      setSelectedMembershipTypesIds(membershipTypeIds);
    }
    dispatch({
      type: Actions.SetRecipientListMemberships,
      payload: {
        membershipTypeIds
      }
    });
  };

  const handleCheckClickedForInterests = (interestId: string) => {
    let interestIds = [];
    if (selectedInterestsIds.includes(interestId)) {
      interestIds = selectedInterestsIds.filter((id) => id !== interestId);
      if (!interestIds.length) {
        setFormErrors({ ...formErrors, interest: ErrorTexts.interest });
      }
      setSelectedInterestsIds(interestIds);
    } else {
      interestIds = [interestId, ...selectedInterestsIds];
      setFormErrors({ ...formErrors, interest: null });
      setSelectedInterestsIds(interestIds);
    }
    dispatch({
      type: Actions.SetRecipientListInterests,
      payload: {
        interestIds
      }
    });
  };

  const handleAllClicked = (section: string) => {
    if (section === RecipientListEnums.Memberships) {
      const membershipTypeIds = membershipTypes?.map((mt) => mt.id);
      setSelectedMembershipTypesIds(membershipTypeIds);
      setFormErrors({ ...formErrors, membership: null });
      dispatch({
        type: Actions.SetRecipientListMemberships,
        payload: {
          membershipTypeIds
        }
      });
    } else if (section === RecipientListEnums.Interests) {
      const interestIds = interests?.map((i) => i.id);
      setSelectedInterestsIds(interestIds);
      setFormErrors({ ...formErrors, interest: null });
      dispatch({
        type: Actions.SetRecipientListInterests,
        payload: {
          interestIds
        }
      });
    }
  };

  return (
    <Box className="step-container">
      <Box className="section-header">GENERAL INFO</Box>
      <Box sx={{ display: 'flex', alignItems: 'center', px: sidePadding, height: '105px' }}>
        <InputComponent
          id="name"
          label="Recipient list name"
          value={name}
          onChange={handleNameChange}
          errorMsg={formErrors.name}
        />
      </Box>
      <Box className="section-header" sx={{ justifyContent: 'space-between' }}>
        <Box>
          MEMBERSHIP
          <span
            className="section-header-info-text"
            style={{
              marginLeft: '5px',
              fontStyle: 'italic'
            }}
          >
            (please select at least one membership)
          </span>
        </Box>
        {selectedMembershipTypesIds.length > 0 ? (
          <Chip
            id={'selected-membership-types-count'}
            data-testid={'selected-membership-types-count'}
            size="small"
            label={`${selectedMembershipTypesIds.length} membership ${
              selectedMembershipTypesIds.length === 1 ? 'option' : 'options'
            } selected`}
            className="header-chip"
            onDelete={() => {
              setSelectedMembershipTypesIds([]);
              setFormErrors({ ...formErrors, membership: ErrorTexts.membership });
              dispatch({
                type: Actions.SetRecipientListMemberships,
                payload: { membershipTypeIds: [] }
              });
            }}
          />
        ) : (
          <SelectAllButton
            onClick={() => handleAllClicked(RecipientListEnums.Memberships)}
            text={`${currentMembershipTypes.length} membership options`}
          />
        )}
      </Box>
      <Box sx={{ px: sidePadding, py: innerPadding, height: '245px' }}>
        <ContentGrid
          data={currentMembershipTypes}
          clickedValues={selectedMembershipTypesIds}
          handleCheckClicked={handleCheckClickedForMembershipTypes}
          editable={true}
        />
        <Box sx={{ color: Colors.red }}>{formErrors.membership}</Box>
      </Box>
      <Box className="section-header" sx={{ justifyContent: 'space-between' }}>
        <Box>
          INTERESTS
          <span
            className="section-header-info-text"
            style={{
              marginLeft: '5px',
              fontStyle: 'italic'
            }}
          >
            (please select at least one interest)
          </span>
        </Box>
        {selectedInterestsIds.length > 0 ? (
          <Chip
            id={'selected-interest-types-count'}
            data-testid={'selected-interest-types-count'}
            size="small"
            label={`${selectedInterestsIds.length} interest ${
              selectedInterestsIds.length === 1 ? 'option' : 'options'
            } selected`}
            className="header-chip"
            onDelete={() => {
              setSelectedInterestsIds([]);
              setFormErrors({ ...formErrors, interest: ErrorTexts.interest });
              dispatch({
                type: Actions.SetRecipientListInterests,
                payload: { interestIds: [] }
              });
            }}
          />
        ) : (
          <SelectAllButton
            onClick={() => handleAllClicked(RecipientListEnums.Interests)}
            text={`${currentInterests.length} interest options`}
          />
        )}
      </Box>
      <Box className="step-content-container" sx={{ height: '119px' }}>
        <ContentGrid
          data={currentInterests}
          clickedValues={selectedInterestsIds}
          handleCheckClicked={handleCheckClickedForInterests}
          editable={true}
        />
        <Box sx={{ color: Colors.red }}>{formErrors.interest}</Box>
      </Box>
    </Box>
  );
};

export default GeneralDetailsTab;
